import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItemButton, ListItemText, Stack, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import { createInterTopicGn, listInterTopicGns, StudentGnDTO } from '../../services/gnStudentService';

interface InterTopicGnListProps {
  courseId: number;
  /** The topic this student is currently viewing — new notebooks are inserted after it. */
  afterNodeId: number;
  onOpen: (gn: StudentGnDTO) => void;
}

/**
 * A student's own private notebooks inserted between topics (spec 046 US3, FR-032). Private
 * to this student, never visible to a tutor, and never touches the course's official
 * structure — this list lives entirely in the student's own view of her course.
 */
const InterTopicGnList: React.FC<InterTopicGnListProps> = ({ courseId, afterNodeId, onOpen }) => {
  const [gns, setGns] = useState<StudentGnDTO[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [refName, setRefName] = useState('');
  const [creating, setCreating] = useState(false);

  const refresh = () => {
    listInterTopicGns(courseId).then(setGns).catch(() => {});
  };

  useEffect(() => { refresh(); }, [courseId]);

  const handleCreate = async () => {
    if (!refName.trim()) return;
    setCreating(true);
    try {
      const gn = await createInterTopicGn(courseId, afterNodeId, refName.trim());
      setRefName('');
      setDialogOpen(false);
      refresh();
      onOpen(gn);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">My Notebooks</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          New notebook here
        </Button>
      </Stack>
      {gns.length > 0 && (
        <List dense disablePadding>
          {gns.map((gn) => (
            <ListItemButton key={gn.id} onClick={() => onOpen(gn)} data-testid={`gn-inter-topic-${gn.id}`}>
              <NoteAddIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              <ListItemText primary={gn.refName} />
            </ListItemButton>
          ))}
        </List>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>New notebook</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Name"
            placeholder="e.g. Formula summary"
            value={refName}
            onChange={(e) => setRefName(e.target.value)}
            sx={{ mt: 1, minWidth: 320 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating || !refName.trim()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InterTopicGnList;
